import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { theme as GlobalTheme } from '../../theme';
import { CueType, StudioCueType } from './Cue';

const TRACK_COLOR = '#474747';
const MARK_COLOR = '#777';

type HighlightKeys = {
  [K in StudioCueType | 'inactive' as `highlight-${K}`]: string;
};

type ThumbKeys = {
  [K in StudioCueType | 'inactive' as `thumb-${K}`]: string;
};

interface BaseSliderClasses {
  slider: string;
  sliderLabels: string;
  middleLabel: string;
  endLabel: string;
}

export type SliderClasses = BaseSliderClasses & HighlightKeys & ThumbKeys;

const generateThumbCSSProperties = (theme: Theme, color: CueType) => ({
  '& .MuiSlider-thumb': {
    backgroundColor: GlobalTheme.palette.slider.thumb[color].backgroundColor,
    '&.Mui-focusVisible, &:hover': {
      boxShadow: `0px 0px 0px ${theme.spacing(1)} ${
        GlobalTheme.palette.slider.thumb[color].boxShadowColor
      }`,
    },
    '&.MuiSlider-active': {
      boxShadow: `0px 0px 0px ${theme.spacing(1.5)} ${
        GlobalTheme.palette.slider.thumb[color].boxShadowColor
      }`,
    },
  },
  '& > .MuiSlider-track': {
    backgroundColor: GlobalTheme.palette.slider.track[color].backgroundColor,
  },
});

const useStyles = makeStyles<{
  trackStyles: { left: string; width: string };
}>()((theme, { trackStyles }) => ({
  slider: {
    '& .MuiSlider-valueLabel': {
      lineHeight: 1.2,
      fontSize: theme.spacing(1.5),
      background: 'unset',
      color: theme.palette.grey[100],
      fontWeight: theme.typography.fontWeightBold,
      textDecoration: 'none',
      padding: theme.spacing(0.5),
      width: theme.spacing(4),
      height: theme.spacing(4),
      borderRadius: '50% 50% 50% 0',
      backgroundColor: 'inherit',
      transformOrigin: 'bottom left',
      transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
      '&::before': { display: 'none' },
      '&.MuiSlider-valueLabelOpen': {
        transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
      },
      '& > *': {
        transform: 'rotate(45deg)',
      },
    },
    '&.MuiSlider-root': {
      color: theme.palette.secondary.light,
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(0.9),
    },
    '& > .MuiSlider-track': {
      color: TRACK_COLOR,
      height: theme.spacing(0.5),
      border: 'none',
      zIndex: 1,
      left: trackStyles.left,
      width: trackStyles.width,
      transition: theme.transitions.create(['left', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shortest,
      }),
    },
    '& > .MuiSlider-rail': {
      height: theme.spacing(0.5),
      backgroundColor: TRACK_COLOR,
      opacity: 1,
      borderRadius: theme.spacing(125),
    },
    '& > .MuiSlider-markLabel': {
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.palette.common.white,
      paddingTop: theme.spacing(1),
    },
    '& > .MuiSlider-mark': {
      height: theme.spacing(0.25),
      width: theme.spacing(0.25),
      backgroundColor: MARK_COLOR,
      borderRadius: 45,
    },
    '& > .MuiSlider-thumb': {
      cursor: 'pointer',
      height: theme.spacing(2.5),
      width: theme.spacing(2.5),
      transition: theme.transitions.create('left', {
        duration: 175,
      }),
      zIndex: 1,
      '&:not(&.MuiSlider-active)': {
        // Slider can get into state where thumb is still focusVisible after user navigates away
        // and boxShadow is applied when we want our slider to be in an inactive state
        '&:not(&:hover)': {
          boxShadow: 'none',
        },
      },
    },
  },
  sliderLabels: {
    // makes no sense why its needed like this here when it works fine above... ??
    fontWeight: theme.typography.fontWeightMedium?.valueOf() as number,
    position: 'absolute',
  },
  middleLabel: {
    left: `calc(50% - ${theme.spacing(2.25)})`,
  },
  endLabel: {
    right: 0,
  },
  /**
   * Highlight classes used on the labels
   */
  'highlight-inactive': {
    color: 'rgba(255, 255, 255, 0.5)',
  },
  /**
   * Thumb classes used on the slider controller
   */
  'thumb-inactive': {
    '& .MuiSlider-thumb': {
      backgroundColor:
        GlobalTheme.palette.slider.thumb.inactive.backgroundColor,
      boxShadow: 'none',
    },
  },
  'thumb-loudness': {
    ...generateThumbCSSProperties(theme, CueType.loudness),
  },
  'thumb-tempo': {
    ...generateThumbCSSProperties(theme, CueType.tempo),
  },
  'thumb-pitch': {
    ...generateThumbCSSProperties(theme, CueType.pitch),
  },
  'thumb-pause': {
    ...generateThumbCSSProperties(theme, CueType.pause),
  },
}));

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export default useStyles;
